import { numberToPrettyString, plural } from '@cian/utils';

import { TAreaUnitType, TLandStatus, TMaterialType } from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { NBSP } from '../../../../constants';
import { IOffer } from '../../../../types/offerData';
import { getBuildMaterialTypeText } from '../../../../utils/build_material_type_text';
import { isBusinessSale } from '../../../../utils/commercial';
import { getCurrencySymbol } from '../../../../utils/formatters';
import { getFloorsRangeWithTotalFloors } from '../../../../utils/getFloorsRangeWithTotalFloors';
import { getFormattedDeadlinePeriod } from '../../../../utils/getFormattedDeadlinePeriod';
import { getLandAreaUnitTypeText } from '../../../../utils/land_area_unit_type_text';
import { getLandStatusText } from '../../../../utils/land_status_text';
import {
  getCommercialArea,
  getCommercialBuildingClass,
  mapAvailableFromToLocaleString,
} from '../../../../utils/offer_helpers';
import { FieldType } from '../config';
import { getPropertyValueMapper } from '../mappers/getPropertyValueMapper';

export function getInfoValue(config: FieldType, offer: IOffer) {
  const { deadline, floorFrom, floorTo, maxArea, minArea, totalArea, monthlyIncome, land, availableFrom } = offer;
  const propertyValueMapper = getPropertyValueMapper(offer);

  const { floorsCount, floorNumber, buildMaterialType, landStatus, landArea } = propertyValueMapper;

  const propertyValue = propertyValueMapper[config.name];

  switch (true) {
    case config.type === 'area': {
      const value = Number(propertyValue);

      return value && !isNaN(value) ? `${numberToPrettyString(value)}${NBSP}м²` : '';
    }

    case config.type === 'deadline':
      return (deadline && !deadline.isComplete && getFormattedDeadlinePeriod(deadline)) || null;

    case config.type === 'deadlineCompleted':
      return (deadline && deadline.isComplete && getFormattedDeadlinePeriod(deadline)) || null;

    case config.type === 'floorNumber': {
      if (floorsCount) {
        if (floorFrom && floorTo) {
          return getFloorsRangeWithTotalFloors('outOfTotal')(floorFrom, floorTo, floorsCount);
        }

        if (floorNumber) {
          return `${floorNumber} из ${floorsCount}`;
        }
      }

      if (floorNumber) {
        return floorNumber;
      }

      return propertyValue;
    }

    case config.type === 'buildMaterialType' && !!buildMaterialType:
      return getBuildMaterialTypeText(buildMaterialType as TMaterialType);

    case config.type === 'landStatus' && !!landStatus:
      return getLandStatusText(landStatus as TLandStatus);

    case config.type === 'landArea' && !!land?.areaUnitType: {
      return Number(landArea) ? `${landArea} ${getLandAreaUnitTypeText(land?.areaUnitType as TAreaUnitType)}` : '';
    }

    case config.type === 'occupied': {
      const availableData = availableFrom ? mapAvailableFromToLocaleString(availableFrom) : null;

      return propertyValue ? (availableData ? `Занято до ${availableData}` : 'Занято') : 'Свободно';
    }

    case config.type === 'commercialFloorsCount': {
      const propertyValueAsNum = Number(propertyValue);

      if (propertyValueAsNum) {
        return `${propertyValueAsNum} ${plural(propertyValueAsNum, ['этаж', 'этажа', 'этажей'])}`;
      }

      return null;
    }

    case config.type === 'ceilingHeight' && !!propertyValue:
      return `${numberToPrettyString(Number(propertyValue))}${NBSP}м`;

    case config.type === 'commercialLandType' && !!propertyValue:
      return `${propertyValue}${land?.possibleToChangeStatus ? ' (можно изменить)' : ''}`;

    case config.type === 'commercialArea':
      return getCommercialArea(minArea, maxArea, totalArea);

    case config.name === 'minLeaseTerm':
      return propertyValue ? `От ${propertyValue} мес.` : '';

    case config.name === 'monthlyIncome': {
      if (!isBusinessSale(offer)) {
        return null;
      }

      const { income, currency } = monthlyIncome || {};

      return income && currency ? `${numberToPrettyString(income)} ${getCurrencySymbol(currency)}/мес.` : '';
    }

    case config.type === 'classType':
      return getCommercialBuildingClass(offer.building, offer.businessShoppingCenter, true);

    default:
      return propertyValue;
  }
}
